import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api'
import { zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { assistentPfad } from '../pfade'
import type { BeobachtungPunkt } from '../typen'

const SCHWERE: Record<BeobachtungPunkt['schwere'], { titel: string; klasse: string }> = {
  warnung: { titel: 'heute', klasse: 'warnung' },
  hinweis: { titel: 'diese Woche', klasse: 'hinweis' },
  tipp: { titel: 'Tipp', klasse: 'gut' },
}

/** „Was heute auffällt": die KI-Synthese über Übersicht, Fällig-Liste und alle Blätter - einmal am
 *  Tag gerechnet, je Punkt mit Link, Frage an den Assistenten und „stimmt nicht". */
export default function Beobachtungen({ gruppe, stichtag }: { gruppe?: string; stichtag?: string }) {
  const [offen, setOffen] = useState(true)
  const { daten, setDaten, laedt: laedtErst, fehler: ladeFehler } = useLaden(() => api.beobachtungen(stichtag, gruppe), [gruppe, stichtag])
  const { fehler: aktionFehler, arbeitet: denktNeu, ausfuehren } = useAktion()
  const laedt = laedtErst || denktNeu
  const fehler = ladeFehler ?? aktionFehler

  /** Nach Buchungen oder Bestellungen: die KI noch einmal über den Tag schauen lassen. */
  function neuDenken() {
    void ausfuehren(async () => setDaten(await api.beobachtungenNeu(stichtag, gruppe)))
  }

  function rueckmelden(p: BeobachtungPunkt, stimmt: boolean) {
    if (!daten) return
    let grund: string | undefined
    if (!stimmt) {
      const eingabe = window.prompt('Was stimmt daran nicht? (kurz, damit es nicht wiederkommt)', '')
      if (eingabe === null) return
      grund = eingabe
    }
    void ausfuehren(async () => {
      const neu = await api.beobachtungRueckmeldung(daten.id, p.nr, stimmt, grund)
      // Die Antwort ist ungefiltert - die Sicht auf das Blatt behalten.
      setDaten({ ...neu, punkte: neu.punkte.filter((x) => !gruppe || x.gruppen.length === 0 || x.gruppen.includes(gruppe)) })
    })
  }

  const punkte = (daten?.punkte ?? []).filter((p) => p.rueckmeldung !== 'stimmt_nicht')
  return (
    <div className={`karte beobachtungen kompakt${offen ? '' : ' eingeklappt'}`}>
      <div className="leiste">
        <b>
          Was heute auffällt{daten && ` (${punkte.length})`}
        </b>
        {daten && <span className="hinweis">gedacht {zeitpunkt(daten.erzeugt_am)}</span>}
        <button onClick={neuDenken} disabled={laedt} title="Nach Buchungen oder Bestellungen neu denken lassen">
          {laedt ? 'denkt nach…' : 'Neu denken'}
        </button>
        <button onClick={() => setOffen((o) => !o)}>{offen ? 'Einklappen' : 'Aufklappen'}</button>
      </div>
      {offen && fehler && <p className="hinweis">Nicht verfügbar: {fehler}</p>}
      {offen && !fehler && laedt && !daten && <p className="hinweis">Die KI liest Übersicht, Fällig-Liste und alle Blätter - beim ersten Mal am Tag bis zu einer Minute.</p>}
      {offen && daten && punkte.length === 0 && <p className="hinweis">Nichts über die Hinweise der Zeilen hinaus.</p>}
      {offen && punkte.length > 0 && (
        <ul className="beobachtungen-liste">
          {punkte.map((p) => (
            <li key={p.nr} className={p.rueckmeldung === 'stimmt' ? 'bestaetigt' : ''}>
              <span className={`marke ${SCHWERE[p.schwere].klasse}`}>{SCHWERE[p.schwere].titel}</span>
              <b> {p.titel}</b> {p.text}
              <span className="beobachtung-aktionen">
                <Link to={p.link}>öffnen</Link>
                {p.frage && <Link to={assistentPfad(p.frage)}>mit dem Assistenten</Link>}
                {p.rueckmeldung !== 'stimmt' && (
                  <button className="leise" onClick={() => rueckmelden(p, true)}>
                    stimmt
                  </button>
                )}
                <button className="leise" onClick={() => rueckmelden(p, false)}>
                  stimmt nicht
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
